import { Config } from '@/config';

/**
 * Website local mode — `npm run dev:local`.
 *
 * It changes nothing about how the app behaves. It only makes the environment
 * visible and switchable:
 *   1. a banner naming the API this page is calling,
 *   2. every API request logged with method, status and duration,
 *   3. `window.fitcutter` for driving it from the console.
 *
 * The two actual behaviour changes in local mode (localhost API, Paddle off)
 * live in config.ts.
 */

const BANNER_ID = 'fitcutter-local-banner';

// ─── Logging ───

export function localLog(...args: unknown[]): void {
  if (!Config.IS_LOCAL) return;
  console.log('%c[fitcutter]', 'color:#ff0033;font-weight:700', ...args);
}

/**
 * fetch wrapper used by services/api.ts. Outside local mode it is a plain
 * pass-through, so production pays nothing for it.
 */
export async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  if (!Config.IS_LOCAL) return fetch(input, init);

  const method = init?.method ?? 'GET';
  const startedAt = performance.now();

  try {
    const response = await fetch(input, init);
    const elapsed = Math.round(performance.now() - startedAt);
    const style = response.ok ? 'color:#16a34a' : 'color:#dc2626;font-weight:700';

    console.log(
      `%c[fitcutter] ${response.status} ${method} ${input} (${elapsed}ms)`,
      style,
    );

    if (response.status === 404) {
      console.warn(
        `[fitcutter] 404 on ${input}\n` +
        `This server may not have that route. With the backend in the dev profile:\n` +
        `  ${Config.BACKEND_URL}/api/public/debug/routes`,
      );
    }

    return response;
  } catch (error) {
    const elapsed = Math.round(performance.now() - startedAt);
    console.error(`[fitcutter] NETWORK FAIL ${method} ${input} (${elapsed}ms)`, error);
    console.warn(
      `[fitcutter] Is the API running at ${Config.BACKEND_URL}? ` +
      `Switch with fitcutter.useBackend('prod') or fitcutter.useBackend('local').`,
    );
    throw error;
  }
}

// ─── Banner ───

function renderBanner(): void {
  if (Config.IS_PRODUCTION_BACKEND && !Config.IS_LOCAL) return;
  if (document.getElementById(BANNER_ID)) return;

  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.className = 'local-banner';

  const label = document.createElement('span');
  label.className = 'local-banner__label';
  label.textContent = Config.IS_PRODUCTION_BACKEND ? 'LOCAL MODE' : 'LOCAL API';
  banner.appendChild(label);

  const target = document.createElement('span');
  target.className = 'local-banner__url';
  target.textContent = Config.BACKEND_URL;
  banner.appendChild(target);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'local-banner__action';
  toggle.textContent = Config.IS_PRODUCTION_BACKEND ? 'Use local API' : 'Use production API';
  toggle.addEventListener('click', () => {
    useBackend(Config.IS_PRODUCTION_BACKEND ? 'local' : 'prod');
  });
  banner.appendChild(toggle);

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'local-banner__dismiss';
  dismiss.textContent = '×';
  dismiss.title = 'Hide for this page load';
  dismiss.addEventListener('click', () => banner.remove());
  banner.appendChild(dismiss);

  document.body.appendChild(banner);
}

// ─── Console API ───

/** Point the site at another API and reload. `local` and `prod` are shorthands. */
function useBackend(url: string): void {
  const resolved =
    url === 'local' ? Config.LOCAL_BACKEND_URL :
    url === 'prod' ? Config.PRODUCTION_BACKEND_URL :
    url.replace(/\/$/, '');

  try {
    localStorage.setItem(Config.BACKEND_OVERRIDE_KEY, resolved);
  } catch {
    console.error('[fitcutter] Could not persist the override — storage is unavailable.');
    return;
  }

  console.log(`[fitcutter] Backend → ${resolved}. Reloading…`);
  window.location.reload();
}

/** Drop the override and go back to the default for this hostname. */
function resetBackend(): void {
  try {
    localStorage.removeItem(Config.BACKEND_OVERRIDE_KEY);
  } catch {
    // Nothing stored anyway.
  }
  window.location.reload();
}

function setLocal(on: boolean): void {
  try {
    if (on) localStorage.setItem(Config.LOCAL_KEY, '1');
    else localStorage.removeItem(Config.LOCAL_KEY);
  } catch {
    console.error('[fitcutter] Could not persist the local-mode flag.');
    return;
  }
  window.location.reload();
}

/** Everything you need to answer "why did that call fail?" in one object. */
function info(): Record<string, unknown> {
  let token: string | null = null;
  let user: string | null = null;
  try {
    token = sessionStorage.getItem('fitaddon_id_token');
    user = sessionStorage.getItem('fitaddon_user');
  } catch {
    // Storage unavailable.
  }

  return {
    backendUrl: Config.BACKEND_URL,
    isProductionBackend: Config.IS_PRODUCTION_BACKEND,
    localMode: Config.IS_LOCAL,
    signedIn: !!token,
    // The token itself is never printed — only enough to tell sessions apart.
    tokenLength: token?.length ?? 0,
    email: user ? (JSON.parse(user) as { email?: string }).email ?? null : null,
    page: window.location.pathname,
  };
}

/** Copy the Google ID token, for curling the API from a terminal. */
async function copyToken(): Promise<void> {
  let token: string | null = null;
  try {
    token = sessionStorage.getItem('fitaddon_id_token');
  } catch {
    // Storage unavailable.
  }

  if (!token) {
    console.warn('[fitcutter] Not signed in — no token to copy.');
    return;
  }

  await navigator.clipboard.writeText(token);
  console.log('[fitcutter] ID token copied. Use it as:  -H "Authorization: Bearer <paste>"');
}

export function initLocalMode(): void {
  // Installed only in local mode. These drive the same overrides config.ts
  // compiles out of a release build, so exposing them there would put the
  // switches back within reach of anyone who can get a line into the console.
  if (!Config.IS_LOCAL) return;

  window.fitcutter = { useBackend, resetBackend, setLocal, info, copyToken };

  renderBanner();
  console.log(
    '%c[fitcutter] local mode',
    'color:#ff0033;font-weight:700',
    info(),
    '\nConsole helpers: fitcutter.useBackend("local"|"prod"|url), ' +
    'fitcutter.resetBackend(), fitcutter.setLocal(false), fitcutter.info(), fitcutter.copyToken()',
  );
}

declare global {
  interface Window {
    /** Local builds only — initLocalMode installs this nowhere else. */
    fitcutter?: {
      useBackend: (url: string) => void;
      resetBackend: () => void;
      setLocal: (on: boolean) => void;
      info: () => Record<string, unknown>;
      copyToken: () => Promise<void>;
    };
  }
}
